<template>
  <div class="ags-nl-search">
    <!-- Search Input -->
    <div class="search-input-wrapper">
      <ThemeIcon icon="search" type="icons" classes="search-icon" />
      <input
        v-model="searchQuery"
        type="text"
        placeholder="Ask about demographics, income, crime, housing..."
        class="search-input"
        @input="onSearchInput"
        @keydown.enter="performSearch"
        @focus="onFocus"
        @blur="onBlur"
      />
      <button
        v-if="searchQuery"
        @click="clearSearch"
        class="clear-button"
        type="button"
        aria-label="Clear search"
      >
        <ThemeIcon icon="x" type="icons" classes="w-4 h-4" />
      </button>
    </div>

    <!-- AI Indicator -->
    <transition name="fade">
      <div v-if="searching" class="ai-indicator">
        <div class="ai-spinner"></div>
        <span>AI is analyzing your query...</span>
      </div>
    </transition>

    <!-- Location Detected -->
    <transition name="fade">
      <div v-if="shouldShowLocation" class="location-indicator">
        <ThemeIcon icon="map-pin" type="icons" classes="w-4 h-4" />
        <span>Location detected: <strong>{{ detectedLocation }}</strong></span>
        <span v-if="shouldShowScope" class="text-xs opacity-75">({{ geographicScope }})</span>
      </div>
    </transition>

    <!-- Search Suggestions -->
    <transition name="slide-down">
      <div v-if="showSuggestions && !searching && !searchResults.length" class="suggestions-panel">
        <div class="suggestions-header">
          <ThemeIcon icon="lightning" type="icons" classes="w-4 h-4" />
          <span>Try asking:</span>
        </div>
        <div
          v-for="(suggestion, index) in suggestions"
          :key="index"
          class="suggestion-item"
          @click="applySuggestion(suggestion)"
        >
          {{ suggestion }}
        </div>
      </div>
    </transition>

    <!-- Search Results -->
    <transition name="slide-down">
      <div v-if="searchResults.length > 0" class="results-panel">
        <div class="results-header">
          <span class="results-count">
            {{ displayedResults.length }} of {{ searchResults.length }} results
          </span>
        </div>

        <div class="results-list">
          <div
            v-for="result in displayedResults"
            :key="`${result.category_id}-${result.variable_path}`"
            class="result-item"
            :class="{ 'result-item--selected': isSelected(result) }"
          >
            <!-- Result Content -->
            <div class="result-content">
              <!-- Result Header -->
              <div class="result-header">
                <span class="result-label">{{ result.label }}</span>
                <span class="result-score">{{ Math.round(result.score * 100) }}%</span>
              </div>

              <!-- Result Details -->
              <div class="result-details">
                <span class="result-category">{{ result.category_display }}</span>
                <span class="result-separator">•</span>
                <span class="result-source">{{ result.source }}</span>
                <span v-if="result.unit" class="result-separator">•</span>
                <span v-if="result.unit" class="result-unit">{{ result.unit }}</span>
              </div>
            </div>

            <!-- Add/Remove Button -->
            <ThemedButton
              v-if="!isSelected(result)"
              variant="default"
              size="icon"
              @click="addVariable(result)"
              class="add-button"
              :title="`Add ${result.label}`"
            >
              <ThemeIcon icon="plus" type="icons" classes="w-4 h-4" />
            </ThemedButton>
            <ThemedButton
              v-else
              variant="outline"
              size="icon"
              @click="removeVariable(result)"
              class="remove-button"
              :title="`Remove ${result.label}`"
            >
              <ThemeIcon icon="check" type="icons" classes="w-4 h-4" />
            </ThemedButton>
          </div>
        </div>

        <!-- Show More Button -->
        <div v-if="hasMoreResults" class="show-more-wrapper">
          <ThemedButton
            variant="ghost"
            size="sm"
            @click="showMoreResults"
            class="show-more-button"
          >
            Show {{ remainingResultsCount }} more results
            <ThemeIcon icon="chevron-down" type="icons" classes="w-4 h-4 ml-2" />
          </ThemedButton>
        </div>
      </div>
    </transition>

    <!-- No Results -->
    <transition name="fade">
      <div v-if="searched && searchResults.length === 0 && !searching" class="no-results">
        <ThemeIcon icon="search" type="icons" classes="w-8 h-8 opacity-50" />
        <p class="no-results-title">No matching variables found</p>
        <p class="no-results-hint">Try rephrasing your query or use different keywords</p>
      </div>
    </transition>

    <!-- Error Message -->
    <transition name="fade">
      <div v-if="error" class="error-message">
        <ThemeIcon icon="alert-circle" type="icons" classes="w-5 h-5" />
        <span>{{ error }}</span>
      </div>
    </transition>
  </div>
</template>

<script>
import ThemeIcon from '../ThemeIcon.vue';
import ThemedButton from '../ThemedButton.vue';
import axios from 'axios';

export default {
  name: 'AgsNaturalLanguageSearch',

  components: {
    ThemeIcon,
    ThemedButton
  },

  props: {
    selectedVariables: {
      type: Array,
      default: () => []
    }
  },

  data() {
    return {
      searchQuery: '',
      searching: false,
      searched: false,
      searchResults: [],
      detectedLocation: null,
      geographicScope: null,
      showSuggestions: false,
      error: null,
      suggestions: [
        'Show me areas with high median income',
        'Find locations with low crime rates',
        'What is the population density?',
        'Show housing affordability metrics',
        'Compare poverty rates across regions'
      ],
      debounceTimer: null,
      cancelTokenSource: null,
      displayLimit: 10,
      initialDisplayLimit: 10
    };
  },

  computed: {
    displayedResults() {
      return this.searchResults.slice(0, this.displayLimit);
    },

    hasMoreResults() {
      return this.searchResults.length > this.displayLimit;
    },

    remainingResultsCount() {
      return this.searchResults.length - this.displayLimit;
    },

    shouldShowLocation() {
      if (this.searching) return false;
      if (!this.detectedLocation) return false;
      
      const invalidValues = ['null', 'none', 'undefined', ''];
      return !invalidValues.includes(String(this.detectedLocation).toLowerCase());
    },

    shouldShowScope() {
      if (!this.geographicScope) return false;
      
      const invalidValues = ['null', 'none', 'undefined', 'unspecified', ''];
      return !invalidValues.includes(String(this.geographicScope).toLowerCase());
    }
  },

  methods: {
    onSearchInput() {
      // Clear previous timer
      if (this.debounceTimer) {
        clearTimeout(this.debounceTimer);
      }

      // Reset error
      this.error = null;

      // If query is too short, clear results immediately
      if (this.searchQuery.length < 3) {
        this.searchResults = [];
        this.searched = false;
        this.detectedLocation = null;
        this.geographicScope = null;
        return;
      }

      // Debounce search - wait 1.5 seconds after user stops typing
      this.debounceTimer = setTimeout(() => {
        this.performSearch();
      }, 1500);
    },

    async performSearch() {
      if (!this.searchQuery || this.searchQuery.length < 3) {
        return;
      }

      // Prevent multiple simultaneous searches
      if (this.searching) {
        return;
      }

      // Cancel previous request if still pending
      if (this.cancelTokenSource) {
        this.cancelTokenSource.cancel('New search initiated');
      }

      // Create new cancel token
      this.cancelTokenSource = axios.CancelToken.source();

      this.searching = true;
      this.showSuggestions = false;
      this.error = null;

      try {
        const response = await axios.get('/api/cresiteiq2/ags/search', {
          params: {
            query: this.searchQuery,
            limit: 10
          },
          timeout: 30000, // 30 second timeout
          cancelToken: this.cancelTokenSource.token
        });

        this.searchResults = response.data.results || [];
        
        // Normalize location values - treat 'none', 'null', etc. as null
        const invalidValues = ['null', 'none', 'undefined', ''];
        const location = response.data.location;
        const scope = response.data.geographic_scope;
        
        this.detectedLocation = (location && !invalidValues.includes(String(location).toLowerCase())) 
          ? location 
          : null;
        
        this.geographicScope = (scope && !invalidValues.includes(String(scope).toLowerCase())) 
          ? scope 
          : null;
        
        this.searched = true;

        console.log('[NL Search] Found', this.searchResults.length, 'results');
        
        if (this.detectedLocation) {
          console.log('[NL Search] Location detected:', this.detectedLocation);
          this.$emit('location-detected', {
            location: this.detectedLocation,
            scope: this.geographicScope
          });
        }

      } catch (error) {
        // Don't show error if request was cancelled
        if (axios.isCancel(error)) {
          console.log('[NL Search] Request cancelled:', error.message);
          return;
        }

        console.error('[NL Search] Search failed:', error);
        
        if (error.code === 'ECONNABORTED') {
          this.error = 'Search timed out. Please try a simpler query.';
        } else if (error.response && error.response.status === 422) {
          this.error = 'Please enter at least 3 characters';
        } else if (error.response && error.response.status === 429) {
          this.error = 'Too many requests. Please wait a moment and try again.';
        } else if (error.response && error.response.status === 500) {
          this.error = 'Server error. Please try again in a moment.';
        } else {
          this.error = 'Search failed. Please try again.';
        }
        
        this.searchResults = [];
      } finally {
        this.searching = false;
        this.cancelTokenSource = null;
      }
    },

    addVariable(result) {
      const variable = {
        path: result.variable_path,
        label: result.label,
        category_name: result.category_name,
        category_id: result.category_id,
        type: result.type,
        unit: result.unit
      };

      this.$emit('variable-selected', variable);
      
      // Show success feedback
      this.$toast?.success(`Added: ${result.label}`);
    },

    removeVariable(result) {
      this.$emit('variable-removed', {
        path: result.variable_path,
        category_id: result.category_id
      });
      this.$toast?.info(`Removed: ${result.label}`);
    },

    isSelected(result) {
      return this.selectedVariables.some(v => 
        v.path === result.variable_path && v.category_id === result.category_id
      );
    },

    applySuggestion(suggestion) {
      this.searchQuery = suggestion;
      this.showSuggestions = false;
      this.performSearch();
    },

    showMoreResults() {
      // Show 5 more results each time
      this.displayLimit = Math.min(
        this.displayLimit + 5,
        this.searchResults.length
      );
    },

    clearSearch() {
      // Cancel any pending request
      if (this.cancelTokenSource) {
        this.cancelTokenSource.cancel('Search cleared');
        this.cancelTokenSource = null;
      }
      
      // Clear debounce timer
      if (this.debounceTimer) {
        clearTimeout(this.debounceTimer);
        this.debounceTimer = null;
      }
      
      // Reset all state
      this.searchQuery = '';
      this.searchResults = [];
      this.searched = false;
      this.showSuggestions = false;
      this.error = null;
      this.detectedLocation = null;
      this.geographicScope = null;
      this.searching = false;
      this.displayLimit = this.initialDisplayLimit;
    },

    clearResults() {
      // Clear everything including search query
      this.clearSearch();
    },

    onFocus() {
      if (!this.searchQuery && !this.searchResults.length) {
        this.showSuggestions = true;
      }
    },

    onBlur() {
      // Delay to allow click on suggestions
      setTimeout(() => {
        this.showSuggestions = false;
      }, 200);
    }
  },

  beforeDestroy() {
    // Cancel any pending request
    if (this.cancelTokenSource) {
      this.cancelTokenSource.cancel('Component destroyed');
    }
    
    // Clear debounce timer
    if (this.debounceTimer) {
      clearTimeout(this.debounceTimer);
    }
  }
};
</script>
